import React, { useState, useMemo } from 'react';
import { UserProfile } from '../types';
import { 
  TrendingUp, Calendar, Globe, AlertTriangle, CheckCircle2, XCircle, 
  Calculator, Building2, Briefcase, Landmark, ArrowRight, Quote,
  ShieldAlert, BookOpen, DollarSign, Percent, Clock, FileText, AlertCircle
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  LineChart, Line
} from 'recharts';

interface InvestmentsProps {
  userProfile: UserProfile;
}

// --- STATIC DATA FOR GUIDANCE ---

const COUNTRY_CONFIG: Record<string, any> = {
  'US': {
    name: 'United States',
    flag: '🇺🇸',
    currency: '$',
    topics: [
      {
        id: '401k',
        title: '401k & IRA Strategy',
        desc: 'Handling US retirement accounts is the #1 financial query for returnees.',
        hasCalculator: true,
        framework: {
          title: 'Should I withdraw my 401k?',
          pros: ['Liquidity for buying a house in India', 'Reset basis to INR (no future currency risk)'],
          cons: ['10% Early Withdrawal Penalty (if < 59.5)', 'Federal + State Tax impact', 'Loss of tax-deferred growth in USD'],
          verdict: 'Most advisors suggest keeping it in the US if you don\'t need the cash immediately. Consider converting to IRA for lower fees.',
          quote: "Don't touch the 401k unless you absolutely need the money for a home purchase. Let it grow in USD."
        }
      },
      {
        id: 'stocks',
        title: 'US Stocks & RSUs',
        desc: 'Capitalizing on the RNOR window for tax-free rebalancing.',
        framework: {
          title: 'Selling US Stocks during RNOR',
          pros: ['0% Capital Gains Tax in India (during RNOR)', 'Reset cost basis for future', 'Diversify into Indian markets'],
          cons: ['Still subject to US Capital Gains tax (if citizen/GC)', 'Currency conversion costs'],
          verdict: 'The "RNOR Reset" is a popular strategy. IMPORTANT: You must file Form W8-BEN with your US broker to certify non-resident status and avoid 30% withholding on capital gains.',
          quote: "I sold everything the year I returned. Zero tax in India due to RNOR. Best decision ever."
        }
      },
      {
        id: '89a',
        title: 'Form 10EE / Section 89A',
        desc: 'Critical compliance for keeping retirement accounts active.',
        alert: 'If you become a ROR (Ordinary Resident) and still hold foreign retirement accounts, you MUST file Form 10EE to defer taxes on the accrued income. Otherwise, India taxes the "unrealized" growth annually.'
      }
    ]
  },
  'UK': {
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: '£',
    topics: [
      {
        id: 'pension',
        title: 'UK Pension Transfers (QROPS)',
        desc: 'Moving pension funds to India is complex but possible.',
        framework: {
          title: 'Transfer Pension to India (QROPS)?',
          pros: ['Consolidate assets in one country', 'Eliminate currency risk', 'Pass on 100% to heirs (UK has restrictions)'],
          cons: ['25% Overseas Transfer Charge (in some cases)', 'Limited investment choices in Indian QROPS', 'Loss of regulatory protection'],
          verdict: 'Only consider if you are permanently retiring in India and have a large pot. Watch out for the 25% exit tax rules.',
          quote: "I left my pension in the UK. The growth has been better than Indian debt funds."
        }
      },
      {
        id: 'isa',
        title: 'ISA Accounts',
        desc: 'ISAs lose their tax-free status once you become a resident in India.',
        alert: 'India does not recognize the tax-free nature of ISAs. Interest and gains generated in an ISA are taxable in India once your RNOR status ends.'
      }
    ]
  },
  'Germany': {
    name: 'Germany',
    flag: '🇩🇪',
    currency: '€',
    topics: [
      {
        id: 'pension_gr',
        title: 'German Pension Refund',
        desc: 'You may be eligible for a refund of your pension contributions if you leave the EU.',
        framework: {
          title: 'Refund vs Wait for Pension',
          pros: ['Lump sum cash injection (~9.3% of gross salary x years)', 'Clean break from German system'],
          cons: ['Must wait 24 months after leaving to apply', 'Employer portion is NOT refunded', 'Lose right to future pension'],
          verdict: 'If you worked < 5 years, refund is often automatic/easy. If > 5 years, you might want to keep the claim for age 67.',
          quote: "I applied for the refund after 2 years. The paperwork was heavy but the lump sum helped buy our Pune flat."
        }
      }
    ]
  }
};

const INDIA_ASSETS = [
  { name: 'NPS (National Pension System)', type: 'Retirement', risk: 'Moderate', returns: '9-11%', tax: 'E-E-E (Tax Free)', notes: 'Best debt vehicle. Lock-in until 60.' },
  { name: 'Equity Mutual Funds', type: 'Growth', risk: 'High', returns: '12-15%', tax: '12.5% on gains > 1.25L', notes: 'SIP recommended. High growth potential.' },
  { name: 'Fixed Deposits (FD)', type: 'Safety', risk: 'Low', returns: '6-7.5%', tax: 'Taxed as Income', notes: 'Good for emergency fund. Low real return.' },
  { name: 'Real Estate (Pune)', type: 'Physical', risk: 'High', returns: '3-4% Yield + Appreciation', tax: 'Indexed Cap Gains', notes: 'Illiquid. Buy for consumption, not just investment.' },
];

const Investments: React.FC<InvestmentsProps> = ({ userProfile }) => {
  const [activeTab, setActiveTab] = useState<'status'|'strategy'|'india'>('status');

  // RNOR Accurate Calculator state
  const [rnorCurrentDays, setRnorCurrentDays] = useState<string>('190');
  const [rnorPrev10Days, setRnorPrev10Days] = useState<string[]>(Array(10).fill('0'));
  const [isCitizenOrPIO, setIsCitizenOrPIO] = useState<boolean>(true);
  const [isLeavingForEmployment, setIsLeavingForEmployment] = useState<boolean>(false);
  const [incomeAbove15L, setIncomeAbove15L] = useState<boolean>(false);
  const [notTaxedInAnyCountry, setNotTaxedInAnyCountry] = useState<boolean>(false);

  // 401k State with Validation
  const [k401Balance, setK401Balance] = useState<string>("100000");
  const [k401Age, setK401Age] = useState<string>("35");
  const [k401TaxRate, setK401TaxRate] = useState<string>("25");

  // Rent vs Buy State with Validation
  const [rentAmount, setRentAmount] = useState<string>("35000");
  const [buyPrice, setBuyPrice] = useState<string>("15000000"); // 1.5 Cr
  const [loanRate, setLoanRate] = useState<string>("8.5");
  const [years, setYears] = useState<string>("10");

  const sourceCountry = userProfile?.sourceCountry || 'US';
  const countryData = COUNTRY_CONFIG[sourceCountry] || COUNTRY_CONFIG['US'];

  // Current Financial Year start year (FY runs Apr 1 – Mar 31)
  const FY_START_YEAR = (() => {
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  })();

  // A04: Insecure Design - Input Validation Logic
  // Prevent unreasonably high numbers that cause chart crashes or NaN errors
  const sanitizeNumber = (val: string, max: number) => {
    const num = parseFloat(val);
    if (isNaN(num)) return 0;
    return Math.min(max, Math.max(0, num));
  };

  const validK401Balance = sanitizeNumber(k401Balance, 10000000); // Max $10M
  const validK401Age = Math.min(100, Math.max(18, parseFloat(k401Age) || 35));
  const validK401TaxRate = Math.min(100, Math.max(0, parseFloat(k401TaxRate) || 0));

  const validRent = sanitizeNumber(rentAmount, 500000); // Max 5L Rent
  const validBuyPrice = sanitizeNumber(buyPrice, 500000000); // Max 50 Cr
  const validLoanRate = Math.min(30, Math.max(0, parseFloat(loanRate) || 0)); // Max 30% interest
  const validYears = Math.min(50, Math.max(1, parseFloat(years) || 1));

  // --- CALCULATORS ---

  // Accurate RNOR calculator implementing Section 6 of Income Tax Act
  const rnorResult = useMemo(() => {
    const D = Math.min(366, Math.max(0, parseInt(rnorCurrentDays) || 0));
    const prev10 = rnorPrev10Days.map(d => Math.min(366, Math.max(0, parseInt(d) || 0)));

    // P4: sum of days in 4 most recent preceding FYs (used in 60/120-day condition B)
    const P4 = prev10.slice(0, 4).reduce((a, b) => a + b, 0);
    // 730-day test: sum of days in 7 most recent preceding FYs
    const DaysLast7 = prev10.slice(0, 7).reduce((a, b) => a + b, 0);
    // 2/10 test: count of preceding FYs where person was Resident (simplified: >= 182 days)
    const ResidentYearsCount10 = prev10.filter(d => d >= 182).length;

    // Step 2: Determine Resident vs Non-Resident for current FY
    let isResident = false;
    if (isLeavingForEmployment) {
      // Leaving India for employment / ship crew — only 182-day rule applies (60-day relaxed)
      isResident = D >= 182;
    } else if (isCitizenOrPIO) {
      // Indian citizen/PIO visiting India from abroad (post-2020 rule)
      isResident = D >= 182 || (incomeAbove15L && D >= 120 && P4 >= 365);
    } else {
      // Generic individual
      isResident = D >= 182 || (D >= 60 && P4 >= 365);
    }

    if (!isResident) {
      return {
        status: 'NR' as const,
        isResident: false,
        D, P4, DaysLast7, ResidentYearsCount10,
        cond1Passed: false, cond2Passed: false,
        isHighIncomeVisitor: false, isDeemedResident: false,
      };
    }

    // Step 4: Special automatic-RNOR cases (post-2020 rules)
    // 120-day rule: visiting NRI/PIO with high income, stays 120-181 days
    const isHighIncomeVisitor =
      isCitizenOrPIO && !isLeavingForEmployment && incomeAbove15L &&
      D >= 120 && D <= 181 && P4 >= 365;
    // Deemed resident: Indian citizen with India income > 15L and not taxed anywhere else
    const isDeemedResident =
      isCitizenOrPIO && incomeAbove15L && notTaxedInAnyCountry;

    if (isHighIncomeVisitor || isDeemedResident) {
      return {
        status: 'RNOR' as const,
        isResident: true,
        D, P4, DaysLast7, ResidentYearsCount10,
        cond1Passed: ResidentYearsCount10 >= 2,
        cond2Passed: DaysLast7 >= 730,
        isHighIncomeVisitor, isDeemedResident,
      };
    }

    // Step 3: Standard ROR vs RNOR — must satisfy BOTH to be ROR, else RNOR
    const cond1Passed = ResidentYearsCount10 >= 2; // Resident in ≥ 2 of last 10 FYs
    const cond2Passed = DaysLast7 >= 730;           // ≥ 730 days in last 7 FYs

    return {
      status: (cond1Passed && cond2Passed) ? 'ROR' as const : 'RNOR' as const,
      isResident: true,
      D, P4, DaysLast7, ResidentYearsCount10,
      cond1Passed, cond2Passed,
      isHighIncomeVisitor: false, isDeemedResident: false,
    };
  }, [rnorCurrentDays, rnorPrev10Days, isCitizenOrPIO, isLeavingForEmployment, incomeAbove15L, notTaxedInAnyCountry]);

  const k401Data = useMemo(() => {
    const growthRate = 0.07;
    const yearsTo60 = Math.max(0, 60 - validK401Age);
    
    const valueAt60 = validK401Balance * Math.pow(1 + growthRate, yearsTo60);
    
    const penalty = validK401Age < 59.5 ? 0.10 : 0;
    const tax = validK401TaxRate / 100;
    const cashNow = validK401Balance * (1 - penalty - tax);
    
    const gradualTax = Math.max(0, validK401TaxRate - 10) / 100;
    const cashGradual = validK401Balance * (1 - penalty - gradualTax);

    return [
      { name: 'Withdraw Now', value: Math.round(cashNow), fill: '#ef4444' }, // Red
      { name: 'Gradual Exit', value: Math.round(cashGradual), fill: '#f59e0b' }, // Amber
      { name: 'Leave to Grow', value: Math.round(valueAt60), fill: '#10b981' } // Green (Future Value)
    ];
  }, [validK401Balance, validK401Age, validK401TaxRate]);

  const rentBuyData = useMemo(() => {
    const investmentReturn = 0.10;
    const propertyAppreciation = 0.04;
    const inflation = 0.05;
    const downPaymentRatio = 0.20;
    
    const downPayment = validBuyPrice * downPaymentRatio;
    const loanAmount = validBuyPrice - downPayment;
    
    const r = validLoanRate / 12 / 100;
    const n = validYears * 12;
    const emi = (r === 0 || n === 0) ? loanAmount / (n || 1) : (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    
    let chartData = [];
    let rentWealth = downPayment;
    let buyWealth = downPayment;
    let currentRent = validRent;

    for (let y = 0; y <= validYears; y++) {
      if (y === 0) {
        chartData.push({ year: y, Rent_Strategy: Math.round(rentWealth/100000), Buy_Strategy: Math.round((validBuyPrice - loanAmount)/100000) });
        continue;
      }

      const annualRent = currentRent * 12;
      const annualEMI = emi * 12;
      const surplus = annualEMI - annualRent;
      
      rentWealth = rentWealth * (1 + investmentReturn);
      rentWealth += surplus; 
      
      currentRent = currentRent * (1 + inflation);

      const propVal = validBuyPrice * Math.pow(1 + propertyAppreciation, y);
      
      const p = y * 12;
      let outstanding = 0;
      if (r > 0 && n > 0) {
         outstanding = (loanAmount * (Math.pow(1+r, n) - Math.pow(1+r, p))) / (Math.pow(1+r, n) - 1);
      } else {
         outstanding = Math.max(0, loanAmount - (emi * p));
      }
      
      buyWealth = propVal - (outstanding > 0 ? outstanding : 0);

      chartData.push({ 
        year: y, 
        Rent_Strategy: Math.round(rentWealth/100000), 
        Buy_Strategy: Math.round(buyWealth/100000) 
      });
    }

    return { data: chartData, emi: Math.round(emi) };
  }, [validRent, validBuyPrice, validLoanRate, validYears]);


  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-24 md:pb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            Financial Roadmap <TrendingUp className="text-emerald-500" />
            <span className="text-xs font-medium px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">🇮🇳 All NRIs</span>
          </h2>
          <p className="text-slate-500 mt-1">
            Navigating taxes and investments from <strong>{countryData.name} {countryData.flag}</strong> to <strong>India 🇮🇳</strong>
          </p>
        </div>
        
        <div className="flex bg-white rounded-lg p-1 border border-slate-200 shadow-sm">
          <button 
            onClick={() => setActiveTab('status')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'status' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            Tax Status
          </button>
          <button 
            onClick={() => setActiveTab('strategy')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'strategy' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            {countryData.flag} Strategy
          </button>
          <button 
            onClick={() => setActiveTab('india')}
            className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'india' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            India Options
          </button>
        </div>
      </div>

      {/* --- TAB 1: TAX STATUS & RNOR --- */}
      {activeTab === 'status' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">

          {/* RNOR Status Calculator */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4">
              <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
                <Calculator size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">RNOR Status Calculator</h3>
                <p className="text-xs text-slate-500">Accurately determine NR / RNOR / ROR under Section 6 of the Income Tax Act</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">

              {/* ── LEFT: Inputs ── */}
              <div className="space-y-5">

                {/* Profile flags */}
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Your Profile</h4>
                  <div className="space-y-2">

                    {/* Citizen / PIO toggle */}
                    <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                      <span className="text-sm font-medium text-slate-700">Indian Citizen or PIO</span>
                      <div className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={isCitizenOrPIO}
                          onChange={e => setIsCitizenOrPIO(e.target.checked)}
                        />
                        <div className="w-9 h-5 bg-slate-300 peer-checked:bg-indigo-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
                      </div>
                    </label>

                    {/* Visit vs Employment (shown for citizens/PIOs) */}
                    {isCitizenOrPIO && (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-500 mb-2 font-medium">Your situation abroad:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setIsLeavingForEmployment(false)}
                            className={`flex-1 py-1.5 text-xs font-bold rounded border transition-colors ${!isLeavingForEmployment ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                          >
                            Visiting India from abroad
                          </button>
                          <button
                            onClick={() => setIsLeavingForEmployment(true)}
                            className={`flex-1 py-1.5 text-xs font-bold rounded border transition-colors ${isLeavingForEmployment ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                          >
                            Left for employment / ship crew
                          </button>
                        </div>
                      </div>
                    )}

                    {/* India income > 15L */}
                    <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                      <span className="text-sm font-medium text-slate-700">India-source taxable income &gt; ₹15 Lakhs this FY</span>
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-indigo-600"
                        checked={incomeAbove15L}
                        onChange={e => setIncomeAbove15L(e.target.checked)}
                      />
                    </label>

                    {/* Deemed resident flag (only relevant when citizen + high income) */}
                    {isCitizenOrPIO && incomeAbove15L && (
                      <label className="flex items-start justify-between p-3 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors gap-3">
                        <div>
                          <span className="text-sm font-medium text-slate-700">Not liable to tax in any other country</span>
                          <p className="text-xs text-amber-600 mt-0.5">May trigger "Deemed Resident" → automatically RNOR</p>
                        </div>
                        <input
                          type="checkbox"
                          className="w-4 h-4 mt-0.5 flex-shrink-0 accent-amber-600"
                          checked={notTaxedInAnyCountry}
                          onChange={e => setNotTaxedInAnyCountry(e.target.checked)}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Current FY days */}
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Days in India — Current FY{' '}
                    <span className="text-indigo-600 normal-case font-bold">
                      FY{FY_START_YEAR}–{(FY_START_YEAR + 1).toString().slice(-2)}
                    </span>
                  </h4>
                  <input
                    type="number" min="0" max="366"
                    value={rnorCurrentDays}
                    onChange={e => setRnorCurrentDays(e.target.value)}
                    className="w-full p-3 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none font-mono text-xl font-bold text-indigo-700 text-center"
                    placeholder="e.g. 190"
                  />
                  <p className="text-xs text-slate-400 mt-1 text-center">
                    Apr {FY_START_YEAR} – Mar {FY_START_YEAR + 1}
                  </p>
                </div>

                {/* Previous 10 FYs table */}
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">
                    Days in India — Previous 10 Financial Years
                  </h4>
                  <p className="text-xs text-slate-400 mb-3">
                    Auto-classifies as Resident if ≥ 182 days. First 7 rows also feed the 730-day test.
                  </p>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-500 font-bold">Financial Year</th>
                          <th className="px-3 py-2 text-center text-slate-500 font-bold">Days in India</th>
                          <th className="px-3 py-2 text-center text-slate-500 font-bold">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {Array.from({ length: 10 }, (_, i) => {
                          const startYear = FY_START_YEAR - 1 - i;
                          const label = `FY${startYear}–${(startYear + 1).toString().slice(-2)}`;
                          const days = Math.min(366, Math.max(0, parseInt(rnorPrev10Days[i]) || 0));
                          const isRes = days >= 182;
                          return (
                            <tr key={i} className={i < 7 ? 'bg-white' : 'bg-slate-50/60'}>
                              <td className="px-3 py-1.5 text-slate-600 font-medium">
                                {label}
                                {i < 7 && (
                                  <span className="ml-1.5 text-indigo-300 text-[10px] font-normal">730d</span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                <input
                                  type="number" min="0" max="366"
                                  value={rnorPrev10Days[i]}
                                  onChange={e => {
                                    const updated = [...rnorPrev10Days];
                                    updated[i] = e.target.value;
                                    setRnorPrev10Days(updated);
                                  }}
                                  className="w-16 px-2 py-1 border border-slate-300 rounded text-center font-mono focus:ring-1 focus:ring-indigo-400 outline-none text-xs"
                                />
                              </td>
                              <td className="px-3 py-1.5 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isRes ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                  {isRes ? 'Resident' : 'NR'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    * Historical status auto-computed using simplified 182-day rule (P4 data not available for past years).
                  </p>
                </div>
              </div>

              {/* ── RIGHT: Results ── */}
              <div className="space-y-4">

                {/* Status badge */}
                <div className={`rounded-xl p-6 text-center text-white shadow-lg ${
                  rnorResult.status === 'NR'
                    ? 'bg-gradient-to-br from-slate-600 to-slate-800'
                    : rnorResult.status === 'RNOR'
                    ? 'bg-gradient-to-br from-emerald-500 to-teal-700'
                    : 'bg-gradient-to-br from-rose-500 to-red-700'
                }`}>
                  <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">
                    Your Tax Residency Status This FY
                  </p>
                  <p className="text-5xl font-black mb-2">{rnorResult.status}</p>
                  <p className="text-sm opacity-80">
                    {rnorResult.status === 'NR'   && 'Non-Resident'}
                    {rnorResult.status === 'RNOR' && 'Resident but Not Ordinarily Resident'}
                    {rnorResult.status === 'ROR'  && 'Resident and Ordinarily Resident'}
                  </p>
                </div>

                {/* Conditions breakdown */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-600 uppercase tracking-widest">How We Got Here</h4>

                  {/* Step 1: Residency test */}
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Step 1 — Residency Test (Section 6)</p>
                    <div className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${
                      rnorResult.isResident
                        ? 'bg-emerald-50 border border-emerald-100 text-emerald-800'
                        : 'bg-slate-100 border border-slate-200 text-slate-600'
                    }`}>
                      {rnorResult.isResident
                        ? <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        : <XCircle size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />}
                      <span>
                        <strong>{rnorResult.D}</strong> days in India this FY
                        {isLeavingForEmployment
                          ? ` (threshold: 182 days — employment rule)`
                          : isCitizenOrPIO
                          ? ` (threshold: 182 days, or 120 days if income > ₹15L and P4 ≥ 365)`
                          : ` (threshold: 182 days, or 60 days if P4 ≥ 365)`}
                        {rnorResult.isResident ? ' → Resident ✓' : ' → Not Resident → NR status'}
                      </span>
                    </div>
                  </div>

                  {rnorResult.isResident && (
                    <>
                      {/* Special rule override */}
                      {(rnorResult.isHighIncomeVisitor || rnorResult.isDeemedResident) && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Special Rule Applied (Post-2020)</p>
                          <div className="flex items-start gap-2 p-2.5 rounded-lg text-xs bg-amber-50 border border-amber-200 text-amber-800">
                            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                            <span>
                              {rnorResult.isHighIncomeVisitor
                                ? `120-day rule: visiting NRI/PIO with India income > ₹15L and ${rnorResult.D} days (120–181) and P4 = ${rnorResult.P4} ≥ 365 → automatically RNOR`
                                : 'Deemed Resident: Indian citizen with India income > ₹15L, not taxed in any other country → automatically RNOR'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Condition 1: 2 of last 10 FYs Resident */}
                      {!rnorResult.isHighIncomeVisitor && !rnorResult.isDeemedResident && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Step 2 — RNOR Condition 1: Resident in ≥ 2 of last 10 FYs</p>
                          <div className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${
                            !rnorResult.cond1Passed
                              ? 'bg-emerald-50 border border-emerald-100 text-emerald-800'
                              : 'bg-amber-50 border border-amber-200 text-amber-800'
                          }`}>
                            {!rnorResult.cond1Passed
                              ? <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                              : <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />}
                            <span>
                              Resident in <strong>{rnorResult.ResidentYearsCount10}</strong> of the last 10 FYs
                              {!rnorResult.cond1Passed
                                ? ' (< 2) → RNOR condition met ✓'
                                : ' (≥ 2) → pushes toward ROR'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Condition 2: 730 days in last 7 FYs */}
                      {!rnorResult.isHighIncomeVisitor && !rnorResult.isDeemedResident && (
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Step 3 — RNOR Condition 2: ≥ 730 days in last 7 FYs</p>
                          <div className={`flex items-start gap-2 p-2.5 rounded-lg text-xs ${
                            !rnorResult.cond2Passed
                              ? 'bg-emerald-50 border border-emerald-100 text-emerald-800'
                              : 'bg-amber-50 border border-amber-200 text-amber-800'
                          }`}>
                            {!rnorResult.cond2Passed
                              ? <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                              : <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />}
                            <span>
                              <strong>{rnorResult.DaysLast7}</strong> total days in last 7 FYs
                              {!rnorResult.cond2Passed
                                ? ' (< 730) → RNOR condition met ✓'
                                : ' (≥ 730) → pushes toward ROR'}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Final verdict */}
                      <div className={`p-3 rounded-lg border text-xs font-medium ${
                        rnorResult.status === 'RNOR'
                          ? 'bg-indigo-50 border-indigo-100 text-indigo-800'
                          : 'bg-rose-50 border-rose-100 text-rose-800'
                      }`}>
                        {rnorResult.status === 'RNOR'
                          ? 'Foreign income is NOT taxed in India this FY. This is your window to rebalance foreign assets tax-efficiently.'
                          : 'Both ROR conditions are met — your global income is now taxable in India. Explore DTAA benefits with a CA.'}
                      </div>
                    </>
                  )}

                  {/* Data summary box */}
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-[10px] text-slate-500 space-y-1 font-mono">
                    <p><span className="font-bold not-italic">D (days this FY):</span> {rnorResult.D}</p>
                    <p><span className="font-bold not-italic">P4 (days, prev 4 FYs):</span> {rnorResult.P4}</p>
                    <p><span className="font-bold not-italic">730-day test (prev 7 FYs):</span> {rnorResult.DaysLast7} / 730</p>
                    <p><span className="font-bold not-italic">2/10 test (resident years):</span> {rnorResult.ResidentYearsCount10} / 10</p>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
            <h4 className="font-bold text-emerald-800 flex items-center gap-2 mb-2">
              <Landmark size={18} /> Why does RNOR status matter?
            </h4>
            <p className="text-sm text-emerald-700 leading-relaxed">
              During RNOR, your foreign income (interest, rent, capital gains) is <strong>NOT taxed in India</strong>.
              Once you become ROR (Ordinarily Resident), your global income becomes taxable in India.
              Returning NRIs typically enjoy <strong>2–3 financial years</strong> of RNOR status — the ideal window
              to sell foreign stocks, rebalance portfolios, and convert NRE accounts.
            </p>
          </div>
        </div>
      )}

      {/* --- TAB 2: COUNTRY STRATEGY --- */}
      {activeTab === 'strategy' && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
           
           {/* 401k CALCULATOR (Only for US) */}
           {sourceCountry === 'US' && (
             <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="bg-orange-100 p-2 rounded-lg text-orange-600">
                    <DollarSign size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">401k Withdrawal Estimator</h3>
                    <p className="text-xs text-slate-500">Visualize the cost of breaking your nest egg early</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                   {/* Inputs */}
                   <div className="space-y-4 md:col-span-1">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">401k Balance ($)</label>
                        <input 
                          type="number" 
                          min="0"
                          max="10000000"
                          value={k401Balance}
                          onChange={(e) => setK401Balance(e.target.value)}
                          className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                        />
                        {parseFloat(k401Balance) < 0 && <span className="text-xs text-red-500 flex items-center gap-1 mt-1"><AlertCircle size={10}/> Cannot be negative</span>}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Current Age</label>
                        <input 
                          type="number" 
                          min="18"
                          max="100"
                          value={k401Age}
                          onChange={(e) => setK401Age(e.target.value)}
                          className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Exp. Tax Bracket (%)</label>
                        <input 
                          type="number" 
                          min="0"
                          max="100"
                          value={k401TaxRate}
                          onChange={(e) => setK401TaxRate(e.target.value)}
                          className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Fed + State estimated tax</p>
                      </div>
                   </div>

                   {/* Chart */}
                   <div className="md:col-span-2 h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={k401Data} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" hide />
                          <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 12, fontWeight: 'bold'}} />
                          <Tooltip 
                            formatter={(value) => [`$${value.toLocaleString()}`, 'Estimated Value']}
                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                          />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                            {k401Data.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <p className="text-center text-xs text-slate-400 italic mt-2">
                        * "Leave to Grow" assumes 7% annual growth until age 60. "Withdraw Now" is net of taxes & penalties.
                      </p>
                   </div>
                </div>
             </div>
           )}

           {/* Static Topics */}
           {countryData.topics.map((topic: any) => (
             <div key={topic.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                   <div className="flex items-center gap-3 mb-2">
                      <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600">
                         {topic.id === '401k' || topic.id.includes('pension') ? <Briefcase size={24} /> : 
                          topic.id === 'stocks' ? <TrendingUp size={24} /> : <AlertTriangle size={24} />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">{topic.title}</h3>
                        <p className="text-sm text-slate-500">{topic.desc}</p>
                      </div>
                   </div>
                   
                   {topic.alert && (
                     <div className="mt-4 bg-amber-50 text-amber-800 p-3 rounded-lg border border-amber-100 text-sm flex items-start gap-2">
                        <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />
                        {topic.alert}
                     </div>
                   )}
                </div>

                {topic.framework && (
                  <div className="bg-slate-50/50 p-6">
                    <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                      <BookOpen size={16} /> Decision Framework: {topic.framework.title}
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                       {/* PROS */}
                       <div className="bg-white p-4 rounded-lg border border-emerald-100 shadow-sm">
                          <h5 className="font-bold text-emerald-700 text-sm mb-3 uppercase flex items-center gap-1">
                             <CheckCircle2 size={14} /> Pros / Benefits
                          </h5>
                          <ul className="space-y-2">
                             {topic.framework.pros.map((p: string, i: number) => (
                               <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                 <span className="text-emerald-500 mt-1">•</span> {p}
                               </li>
                             ))}
                          </ul>
                       </div>

                       {/* CONS */}
                       <div className="bg-white p-4 rounded-lg border border-red-100 shadow-sm">
                          <h5 className="font-bold text-red-700 text-sm mb-3 uppercase flex items-center gap-1">
                             <XCircle size={14} /> Cons / Risks
                          </h5>
                          <ul className="space-y-2">
                             {topic.framework.cons.map((c: string, i: number) => (
                               <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                 <span className="text-red-500 mt-1">•</span> {c}
                               </li>
                             ))}
                          </ul>
                       </div>
                    </div>

                    <div className="bg-indigo-900 text-white p-4 rounded-xl shadow-lg relative overflow-hidden">
                       <Quote size={80} className="absolute -top-2 -right-2 text-indigo-800 opacity-50" />
                       <p className="font-medium italic relative z-10 mb-2">"{topic.framework.quote}"</p>
                       <div className="flex items-center gap-2 text-xs text-indigo-200 font-bold uppercase tracking-wider relative z-10">
                          <Globe size={12} /> Community Wisdom
                       </div>
                    </div>
                  </div>
                )}
             </div>
           ))}
        </div>
      )}

      {/* --- TAB 3: INDIA OPTIONS --- */}
      {activeTab === 'india' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
           
           {/* RENT VS BUY CALCULATOR */}
           <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-6 border-b border-slate-100 pb-4">
                  <div className="bg-purple-100 p-2 rounded-lg text-purple-600">
                    <Building2 size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Rent vs Buy Analyzer</h3>
                    <p className="text-xs text-slate-500">Should you buy a flat in Pune immediately?</p>
                  </div>
              </div>

              <div className="grid md:grid-cols-12 gap-8">
                 {/* Controls */}
                 <div className="md:col-span-4 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Monthly Rent Budget (₹)</label>
                      <input 
                        type="number" 
                        min="0"
                        max="500000"
                        value={rentAmount}
                        onChange={(e) => setRentAmount(e.target.value)}
                        className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Property Price (₹)</label>
                      <input 
                        type="number" 
                        min="0"
                        max="500000000"
                        value={buyPrice}
                        onChange={(e) => setBuyPrice(e.target.value)}
                        className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                      />
                      <p className="text-[10px] text-slate-400 text-right mt-1">{(validBuyPrice/10000000).toFixed(2)} Cr</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                       <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Loan Rate (%)</label>
                          <input 
                            type="number" 
                            min="0"
                            max="30"
                            value={loanRate}
                            onChange={(e) => setLoanRate(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                          />
                       </div>
                       <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Years</label>
                          <input 
                            type="number" 
                            min="1"
                            max="30"
                            value={years}
                            onChange={(e) => setYears(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-purple-500 outline-none font-mono"
                          />
                       </div>
                    </div>
                    
                    <div className="bg-slate-50 p-3 rounded border border-slate-200 mt-4">
                       <p className="text-xs text-slate-500 font-bold uppercase">Resulting EMI</p>
                       <p className="text-xl font-bold text-slate-800">₹{rentBuyData.emi.toLocaleString()}</p>
                       {rentBuyData.emi > validRent && (
                         <p className="text-xs text-purple-600 mt-1">
                           Surplus invested in Rent Strategy: <br/><strong>₹{(rentBuyData.emi - validRent).toLocaleString()}/mo</strong>
                         </p>
                       )}
                    </div>
                 </div>

                 {/* Chart */}
                 <div className="md:col-span-8 h-80">
                    <p className="text-center text-sm font-bold text-slate-700 mb-2">Projected Net Wealth (₹ Lakhs)</p>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={rentBuyData.data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" label={{ value: 'Years', position: 'insideBottomRight', offset: -10 }} />
                        <YAxis />
                        <Tooltip 
                           formatter={(value) => [`₹${value} L`, 'Net Wealth']}
                           contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                        <Legend verticalAlign="top" height={36}/>
                        <Line type="monotone" dataKey="Rent_Strategy" stroke="#10b981" strokeWidth={3} name="Rent & Invest" />
                        <Line type="monotone" dataKey="Buy_Strategy" stroke="#8b5cf6" strokeWidth={3} name="Buy Property" />
                      </LineChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-slate-400 mt-2 text-center max-w-lg mx-auto">
                      * Assumes 20% downpayment. Rent increases 5%/yr. Property appreciates 4%/yr (conservative Pune estimate). Investments grow 10%/yr.
                    </p>
                 </div>
              </div>
           </div>

           <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <h3 className="text-lg font-bold text-slate-800 mb-4">Investment Basket for Returnees</h3>
             <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                   <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                         <th className="p-3 font-bold text-slate-600">Asset Class</th>
                         <th className="p-3 font-bold text-slate-600">Avg Returns</th>
                         <th className="p-3 font-bold text-slate-600">Taxability</th>
                         <th className="p-3 font-bold text-slate-600">Suitability</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-100">
                      {INDIA_ASSETS.map((asset, i) => (
                         <tr key={i} className="hover:bg-slate-50/50">
                            <td className="p-3">
                               <div className="font-bold text-slate-800">{asset.name}</div>
                               <div className="text-xs text-slate-500">{asset.type} • {asset.risk} Risk</div>
                            </td>
                            <td className="p-3 font-medium text-emerald-600">{asset.returns}</td>
                            <td className="p-3 text-slate-600">{asset.tax}</td>
                            <td className="p-3 text-slate-600 max-w-xs">{asset.notes}</td>
                         </tr>
                      ))}
                   </tbody>
                </table>
             </div>
           </div>

           <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col items-center text-center">
                 <div className="bg-orange-100 p-3 rounded-full text-orange-600 mb-3">
                    <Building2 size={32} />
                 </div>
                 <h4 className="font-bold text-slate-800 mb-2">Real Estate Warning</h4>
                 <p className="text-sm text-slate-600">
                   Rental yields in Pune are low (2-3%). Don't buy real estate purely for investment income. Buy it if you intend to live in it.
                 </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col items-center text-center">
                 <div className="bg-blue-100 p-3 rounded-full text-blue-600 mb-3">
                    <Landmark size={32} />
                 </div>
                 <h4 className="font-bold text-slate-800 mb-2">NRE vs Resident Accounts</h4>
                 <p className="text-sm text-slate-600">
                   Once you become a resident, you legally must redesignate NRE accounts to Resident accounts. FDs can usually continue until maturity.
                 </p>
              </div>
           </div>
        </div>
      )}

      {/* DISCLAIMER FOOTER */}
      <div className="mt-12 border-t border-slate-200 pt-6 text-center">
         <p className="text-xs text-slate-400 max-w-2xl mx-auto">
           <strong>Disclaimer:</strong> This tool provides general information based on community experiences and is not professional financial or tax advice. Tax laws in India, US, UK, and Germany change frequently. Please consult a qualified Chartered Accountant (CA) or CPA before making significant financial moves.
         </p>
         
         <div className="mt-4 flex justify-center gap-4">
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
               Recommended: Fee-Only SEBI Registered Advisor
            </span>
            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
               Recommended: Cross-Border Tax CA
            </span>
         </div>
      </div>
    </div>
  );
};

export default Investments;